import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, AlertTriangle, CheckCircle2, X } from 'lucide-react';

/**
 * Formate une durée en minutes de manière lisible
 * @param {number} minutes - Nombre de minutes
 * @returns {string} - Durée formatée (ex: "45 min", "2h 30min", "1j 3h")
 */
const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes < 0) return '0 min';

  const totalMinutes = Math.floor(minutes);

  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (hours < 24) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (remainingHours > 0) {
    return `${days}j ${remainingHours}h`;
  }

  return `${days}j`;
};

/**
 * StatusNotificationsModal
 * Bouton flottant de notifications qui s'ouvre en pop-up:
 * - Visible seulement quand il y a des notifications actives
 * - Clic pour ouvrir/fermer la pop-up
 * - Comme le bouton BackToTop mais pour les notifications
 *
 * Props:
 * - notifications: Array<{ key, type, id, name, since, minutes }>
 */
const StatusNotificationsModal = ({
  notifications = [],
  unreadNotifications = [],
  onMarkAsRead
}) => {
  const { t } = useTranslation();

  // Le bouton reste visible tant qu'il y a des notifications actives (DOWN ou réouvertures récentes)
  const hasActiveNotifications = notifications && notifications.length > 0;
  const hasUnreadNotifications = unreadNotifications && unreadNotifications.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const [isHiding] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const items = useMemo(() => {
    // Trier: les réouvertures en haut (plus "positives"), puis pannes
    const arr = Array.isArray(notifications) ? [...notifications] : [];
    return arr.sort((a, b) => {
      if (a.type === b.type) return 0;
      return a.type === 'reopen' ? -1 : 1;
    });
  }, [notifications]);

  // Gérer la fermeture de la popup (ne masque pas le bouton automatiquement)
  function handleClose() {
    setIsClosing(true);
    // Attendre la fin de l'animation de la popup
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      // NE PAS marquer automatiquement comme lu ni masquer le bouton
      // Le bouton reste visible tant qu'il y a des notifications actives
    }, 300); // Durée de l'animation popupSlideOut
  }

  // Fermer automatiquement après 5 secondes si ouvert
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Ne rien afficher si pas de notifications actives et pas en train de se cacher
  if (!hasActiveNotifications && !isHiding) return null;

  const togglePopup = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);

    // Marquer les notifications comme lues quand on ouvre le popup
    if (willOpen && onMarkAsRead) {
      onMarkAsRead();
    }
  };

  // Compter les types de notifications non lues
  const downCount = unreadNotifications.filter(n => n.type === 'down').length;
  const reopenCount = unreadNotifications.filter(n => n.type === 'reopen').length;
  const getReopenLabel = (notification) => {
    const baseLabel = notification.isRecentlyReopened
      ? t('notifications.status.recentlyReopened')
      : `${t('notifications.status.reopened')} ${formatDuration(notification.minutes)}`;
    const duration = Number.isFinite(notification.durationMinutes)
      ? formatDuration(notification.durationMinutes)
      : null;

    if (!duration) {
      return baseLabel;
    }

    return `${baseLabel} (${t('notifications.status.outageDuration', {
      duration,
      defaultValue: `panne de ${duration}`
    })})`;
  };

  return (
    <>
      {/* Bouton flottant */}
      <button
        className={`status-notification-bell visible ${isOpen ? 'active' : ''} ${isHiding ? 'hiding' : ''} ${hasUnreadNotifications ? 'has-unread' : 'no-unread'}`}
        onClick={togglePopup}
        aria-label={`${notifications.length} notification${notifications.length > 1 ? 's' : ''} d'état`}
        title={`${downCount} ${t('notifications.status.interrupted')}${downCount > 1 ? 's' : ''}, ${reopenCount} réouverture${reopenCount > 1 ? 's' : ''}`}
      >
        <Bell size={20} />
        {hasUnreadNotifications && (
          <span className="notification-badge">{unreadNotifications.length}</span>
        )}
      </button>

      {/* Pop-up */}
      {(isOpen || isClosing) && (
        <div className={`status-notification-popup ${isClosing ? 'closing' : ''}`}>
          <div className="popup-header">
            <h2 className="popup-title">
              <Bell size={16} />
              {t('notifications.title')}
            </h2>
            <button
              type="button"
              className="popup-close"
              onClick={handleClose}
              aria-label="Fermer"
            >
              <X size={16} />
            </button>
          </div>
          <div className="popup-body">
            <div className="status-notifications-list">
              {items.map((n) => (
                <div className={`status-notif-item ${n.type}`} key={n.key}>
                  <div className="status-notif-icon">
                    {n.type === 'down' ? (
                      <AlertTriangle size={16} />
                    ) : (
                      <CheckCircle2 size={16} />
                    )}
                  </div>
                  <div className="status-notif-text">
                    {n.type === 'down' ? (
                      <span>
                        <strong>{n.name}</strong> {t('notifications.status.interrupted')}{n.since ? ` ${t('notifications.status.since')} ${formatDuration(n.minutes)}` : ''}
                      </span>
                    ) : (
                      <span>
                        <strong>{n.name}</strong> {getReopenLabel(n)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StatusNotificationsModal;
